import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, ofType, createEffect } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  map,
  tap,
  switchMap,
  catchError,
  withLatestFrom
} from 'rxjs/operators';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { of } from 'rxjs';
import { notificationMessages } from '@care-portals/data';
import { calcDiscountAmount } from '@care-portals/shared';
import { ICustomCartLineItem } from '@care-portals/api-interfaces';
import { Angulartics2GoogleTagManager } from 'angulartics2';
import { APIService } from '../../services/api/api-service';
import * as CheckoutActions from './checkout.actions';
import * as CheckoutSelectors from './checkout.selectors';

@Injectable()
export class CheckoutEffects {
  constructor(
    private actions$: Actions,
    private router: Router,
    private notification: NzNotificationService,
    private apiService: APIService,
    private gtm: Angulartics2GoogleTagManager,
    private store: Store
  ) {}

  loadCart$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCart),
      switchMap(({ cartId }) =>
        this.apiService
          .getCartData(cartId)
          .pipe(map(cart => CheckoutActions.loadCartSuccess({ cart })))
      ),
      catchError(error => of(CheckoutActions.loadCartFailure(error)))
    )
  );

  linkCartToCustomer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.linkCartToCustomer),
      switchMap(({ cartId }) =>
        this.apiService
          .linkCartToCustomer(cartId)
          .pipe(map(cart => CheckoutActions.loadCartSuccess({ cart })))
      ),
      catchError(error => of(CheckoutActions.loadCartFailure(error)))
    )
  );

  loadCartSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadCartSuccess),
      // switchMap(res => this.userService.getStripeDashboardUrl()
      //   .pipe(
      //     map(url => CheckoutActions.goToStripeDashboardSuccess({ url }))
      //   )
      // ),
      // catchError(error => of(CheckoutActions.goToStripeDashboardFailure(error)))
      switchMap(({ cart }) => {
        this.gtm.eventTrack('checkout-started', {
          category: 'checkout',
          label: 'checkout-started',
          value: cart.cart_amount,
          gtmCustom: {
            cart,
            items: cart?.line_items?.physical_items?.map(item => ({
              item_name: item.name,
              item_id: item.sku,
              quantity: item.quantity,
              price: item.list_price
            }))
          }
        });
        return of(CheckoutActions.setOrderItems());
      })
    )
  );

  loadCartFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.loadCartFailure),
        tap(({ error }) => {
          console.error(error);
          this.notification.error(
            notificationMessages.InvalidCart.title,
            notificationMessages.InvalidCart.message
          );
        })
      ),
    { dispatch: false }
  );

  setOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.setOrderItems),
      withLatestFrom(
        this.store.select(CheckoutSelectors.selectCart),
        this.store.select(CheckoutSelectors.selectCoupon)
      ),
      switchMap(([action, cart, coupon]) => {
        let orderItems = cart?.line_items
          ? [
              ...cart?.line_items?.physical_items,
              ...cart?.line_items?.custom_items,
              ...cart?.line_items?.digital_items
            ]
          : [];

        const couponDiscountAmount = calcDiscountAmount(cart, coupon);

        if (couponDiscountAmount > 0) {
          const discountItem: ICustomCartLineItem = {
            sku: coupon.code,
            name: 'Coupon Discount',
            type: 'coupon',
            quantity: 1,
            discount_amount: couponDiscountAmount,
            list_price: -couponDiscountAmount,
            sale_price: -couponDiscountAmount,
            extended_list_price: -couponDiscountAmount,
            extended_sale_price: -couponDiscountAmount
          };
          orderItems = [...orderItems, discountItem];
        }

        return [CheckoutActions.setOrderItemsSuccess({ orderItems })];
      })
    )
  );

  createOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.createOrder),
      switchMap(({ newOrder }) =>
        this.apiService.createOrder(newOrder).pipe(
          map(res =>
            CheckoutActions.createOrderSuccess({ orderId: res.id, order: res })
          ),
          catchError(error => of(CheckoutActions.createOrderFailure(error)))
        )
      ),
      catchError(error => of(CheckoutActions.createOrderFailure(error)))
    )
  );

  createOrderSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.createOrderSuccess),
        tap(({ orderId, order }) => {
          this.gtm.eventTrack('order-created', {
            category: 'checkout',
            label: 'order-created',
            value: order.totalAmount,
            gtmCustom: {
              orderId,
              order
            }
          });

          // this.router.navigate(['consultation', 'start'], {
          //   queryParams: { orderId }
          // });

          // delay to avoid race condition
          // to avoid in patient-api Error: This invoice is already finalized, you can't re-finalize a non-draft invoice.
          setTimeout(() => {
            this.router.navigate(['orders', orderId]);
          }, 2000);
        })
      ),
    { dispatch: false }
  );

  createOrderFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CheckoutActions.createOrderFailure),
        tap(({ error }) => {
          this.notification.error(
            notificationMessages.OrderFailed.title,
            notificationMessages.OrderFailed.message
          );
        })
      ),
    { dispatch: false }
  );

  loadAddresses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.loadAddresses),
      switchMap(action =>
        this.apiService
          .getUserAddresses()
          .pipe(
            map(addresses =>
              CheckoutActions.loadAddressesSuccess({ addresses })
            ),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                'Error',
                'There was an error getting addresses.'
              );
              return of(CheckoutActions.loadAddressesFailure({ error }));
            })
          )
      )
    )
  );

  applyCoupon$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.applyCoupon),
      withLatestFrom(this.store.select(CheckoutSelectors.selectCart)),
      switchMap(([action, cart]) =>
        this.apiService
          .checkApplyCoupon(action.cartId || cart.id, action.code)
          .pipe(
            map(resp => {
              if (resp.coupon) {
                const couponDiscount = calcDiscountAmount(cart, resp.coupon);

                const promotionDiscount = cart.base_amount - cart.cart_amount;

                if (couponDiscount < promotionDiscount) {
                  this.notification.info(
                    'Coupon was not applied!',
                    'Your current discount is larger than the coupon discount'
                  );
                  return CheckoutActions.nullAction();
                }

                this.gtm.eventTrack('coupon_applied', {
                  category: 'checkout',
                  label: 'coupon_applied',
                  // value: should reflect the new cart totalAmount after discount
                  gtmCustom: {
                    coupon: resp?.coupon?.code
                  }
                });
                return CheckoutActions.applyCouponSuccess({
                  coupon: resp.coupon
                });
              } else {
                this.notification.error('Error', resp.error);
                return CheckoutActions.nullAction();
              }
            }),
            catchError(error => {
              console.error('Error', error);
              this.notification.error(
                'Error',
                'There was an error applying coupon.</br> Please make sure you are logged in.'
              );
              return of(CheckoutActions.applyCouponFailure({ error }));
            })
          )
      )
    )
  );

  applyCouponSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CheckoutActions.applyCouponSuccess),
      switchMap(_action => of(CheckoutActions.setOrderItems()))
    )
  );

  // saveAddresses$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CheckoutActions.saveAddresses),
  //     fetch({
  //       run: action => {
  //         return this.apiService.saveAddresses(action.addressDTO).pipe(
  //           map(addresses => CheckoutActions.saveAddressesSuccess({ addresses }))
  //         );
  //       },

  //       onError: (action, error) => {
  //         console.error('Error', error);
  //         this.notification.error('Error', 'There was an error getting addresses.');
  //         return CheckoutActions.saveAddressesFailure({ error });
  //       }
  //     })
  //   )
  // );

  // setShippingAddress$ = createEffect(() =>
  //   this.actions$.pipe(
  //     ofType(CheckoutActions.setShippingAddress),
  //     fetch({
  //       run: action => {
  //         return this.apiService.setShippingAddress(action.cartId, action.address).pipe(
  //           tap((res) => {
  //             this.notification.success('Successfully saved shipping address.', '');
  //           }),
  //           map(res => CheckoutActions.setShippingAddressSuccess({ res }))
  //         );
  //       },

  //       onError: (action, error) => {
  //         console.error('Error', error);
  //         this.notification.error('Error', 'There was an error setting shipping address.');
  //         return CheckoutActions.setShippingAddressFailure({ error });
  //       }
  //     })
  //   )
  // );
}
